import type {
	BranchStructureChangeDto,
	HistoryWithDeploymentDto,
	MergeEventDtoRead,
	MergeRequestApprovalDtoRead,
	MergeRequestConflictResolutionDtoRead,
	MergeRequestDecisionEnum,
	MergeRequestDtoRead,
	MergeRequestStatusEnum,
	UserInfoDtoRead,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { toLocaleDateString } from '@/utils/numbers'

import { BranchRecord } from '../Branches/types'
import { DiffEngine } from './diffEngine'
import {
	DecisionKind,
	MergeRequestRecord,
	MergeRequestStatus,
	MrApproval,
	MrStructure,
	MrTimelineEvent,
	StructureSnapshot,
	StructureType,
} from './types'

const toStatus = (status?: MergeRequestStatusEnum): MergeRequestStatus => {
	if (status === 'DRAFT') return 'OPEN'
	if (
		status === 'OPEN' ||
		status === 'CHANGES_REQUESTED' ||
		status === 'APPROVED' ||
		status === 'MERGED' ||
		status === 'REJECTED' ||
		status === 'CLOSED' ||
		status === 'STALE'
	) {
		return status
	}
	return 'OPEN'
}

const toDecision = (decision?: MergeRequestDecisionEnum): DecisionKind => {
	if (decision === 'REQUEST_CHANGES') return 'CHANGES_REQUESTED'
	if (decision === 'REJECT') return 'REJECTED'
	return 'APPROVED'
}

export const toApiDecision = (
	decision: DecisionKind,
): MergeRequestDecisionEnum => {
	if (decision === 'CHANGES_REQUESTED') return 'REQUEST_CHANGES'
	if (decision === 'REJECTED') return 'REJECT'
	return 'APPROVE'
}

const userLabel = (user?: UserInfoDtoRead) =>
	user?.fullName?.trim() || user?.loginName?.trim() || ''

const authorLabel = (dto: MergeRequestDtoRead) =>
	userLabel(dto.author) || dto.createdBy || ''

const sortByAt = (left: MrTimelineEvent, right: MrTimelineEvent) =>
	(left.sortAt ?? left.at).localeCompare(right.sortAt ?? right.at)

export const mapTimeline = (
	dto: MergeRequestDtoRead,
	approvals: MergeRequestApprovalDtoRead[] = [],
	mergeEvents: MergeEventDtoRead[] = [],
): MrTimelineEvent[] => {
	const events: MrTimelineEvent[] = []
	if (dto.createdAt) {
		events.push({
			at: toLocaleDateString(dto.createdAt),
			sortAt: dto.createdAt,
			kind: 'created',
			actor: authorLabel(dto),
			text: '',
		})
	}
	for (const approval of approvals) {
		const decision = toDecision(approval.decision)
		events.push({
			at: toLocaleDateString(approval.decidedAt),
			sortAt: approval.decidedAt ?? '',
			kind:
				decision === 'CHANGES_REQUESTED'
					? 'changes'
					: decision === 'REJECTED'
						? 'rejected'
						: 'approved',
			actor: userLabel(approval.user),
			text: approval.comment ?? '',
		})
	}
	for (const event of mergeEvents) {
		events.push({
			at: toLocaleDateString(event.executedAt),
			sortAt: event.executedAt ?? '',
			kind: 'merged',
			actor: userLabel(event.executedByUser),
			text: event.mergeSummary ?? '',
		})
	}
	if (dto.closedAt) {
		events.push({
			at: toLocaleDateString(dto.closedAt),
			sortAt: dto.closedAt,
			kind: 'closed',
			actor: userLabel(dto.closedBy),
			text: '',
		})
	}
	return events.sort(sortByAt)
}

export const mapApprovals = (
	approvals: MergeRequestApprovalDtoRead[] | undefined,
): MrApproval[] =>
	(approvals ?? [])
		.filter((approval) => approval.active !== false)
		.map((approval) => ({
			userId: approval.user?.id != null ? String(approval.user.id) : '',
			name: userLabel(approval.user),
			decision: toDecision(approval.decision),
			at: toLocaleDateString(approval.decidedAt),
			comment: approval.comment ?? '',
		}))

export const mapMergeRequestDto = (
	dto: MergeRequestDtoRead,
	branches: BranchRecord[],
	approvals: MergeRequestApprovalDtoRead[] = [],
	resolutions: MergeRequestConflictResolutionDtoRead[] = [],
	mergeEvents: MergeEventDtoRead[] = [],
): MergeRequestRecord => {
	const target = branches.find((branch) => branch.id === String(dto.targetBranchId))
	return {
		id: String(dto.id ?? ''),
		systemId: '',
		title: dto.title ?? '',
		description: dto.description ?? '',
		sourceBranchId: String(dto.sourceBranchId ?? ''),
		targetBranchId: String(dto.targetBranchId ?? ''),
		author: authorLabel(dto),
		authorId: dto.author?.id ?? null,
		status: toStatus(dto.status),
		createdAt: toLocaleDateString(dto.createdAt),
		requiredApprovals: dto.requiredApprovals ?? target?.requiredApprovals ?? 1,
		selfApprovalAllowed:
			dto.selfApprovalAllowed ?? target?.selfApprovalAllowed ?? false,
		requireExplicitMergeAction: Boolean(target?.requireExplicitMergeAction),
		targetMoved: false,
		scope: dto.scope === 'PARTIAL' ? 'PARTIAL' : 'ALL',
		structures: (dto.changes ?? []).map((change) =>
			applyResolutions(changeToMrStructure(change), resolutions, change.structureId),
		),
		approvals: mapApprovals(approvals),
		timeline: mapTimeline(dto, approvals, mergeEvents),
		mergedAuto: false,
		approvalCount: dto.approvalCount,
		changes: dto.changes ?? [],
		diffSummary: dto.diffSummary,
	}
}

type PathToken = { identity?: string; key: string }

const tokenizePath = (path: string): PathToken[] => {
	const body = path.replace(/^\$\.?/, '')
	if (!body) return []
	const tokens: PathToken[] = []
	const re = /([^[.\]]+)|\[([^\]]+)\]/g
	let pending: PathToken | null = null
	let match: RegExpExecArray | null
	while ((match = re.exec(body))) {
		if (match[1]) {
			pending = { key: match[1] }
			tokens.push(pending)
		} else if (match[2] && pending) {
			pending.identity = match[2]
		} else if (match[2]) {
			tokens.push({ key: '', identity: match[2] })
		}
	}
	return tokens
}

const cloneJson = (value: unknown) => {
	if (value == null || typeof value !== 'object') return value
	return JSON.parse(JSON.stringify(value)) as unknown
}

const identityFields = (identity: string): Record<string, string> => {
	const idx = identity.indexOf(':')
	if (idx < 0) return { code: identity }
	return { [identity.slice(0, idx)]: identity.slice(idx + 1) }
}

const ensureArrayItem = (
	arr: Record<string, unknown>[],
	identity: string,
): Record<string, unknown> => {
	const fields = identityFields(identity)
	const found = arr.find((item) =>
		Object.entries(fields).every(([key, val]) => String(item[key]) === val),
	)
	if (found) return found
	const item = { ...fields }
	arr.push(item)
	return item
}

const setPath = (
	root: Record<string, unknown>,
	path: string,
	value: unknown,
) => {
	const tokens = tokenizePath(path)
	if (!tokens.length) {
		if (value && typeof value === 'object' && !Array.isArray(value)) {
			Object.assign(root, cloneJson(value) as object)
		}
		return
	}
	let cursor: Record<string, unknown> = root
	tokens.forEach((token, index) => {
		const last = index === tokens.length - 1
		if (token.identity) {
			const key = token.key || 'items'
			if (!Array.isArray(cursor[key])) cursor[key] = []
			const item = ensureArrayItem(
				cursor[key] as Record<string, unknown>[],
				token.identity,
			)
			if (last) {
				if (value && typeof value === 'object' && !Array.isArray(value)) {
					Object.assign(item, cloneJson(value) as object)
				}
			} else {
				cursor = item
			}
			return
		}
		if (last) {
			cursor[token.key] = cloneJson(value)
			return
		}
		if (cursor[token.key] == null || typeof cursor[token.key] !== 'object') {
			cursor[token.key] = {}
		}
		cursor = cursor[token.key] as Record<string, unknown>
	})
}

/** Backend field paths are `$.columns[uuid:x].name`; DiffEngine node paths are `$/columns/uuid:x/name`. */
export const toEnginePath = (fieldPath: string): string => {
	let path = '$'
	for (const token of tokenizePath(fieldPath)) {
		if (token.key) path += `/${token.key}`
		if (token.identity) path += `/${token.identity}`
	}
	return path
}

export const toApiFieldPath = (enginePath: string): string => {
	let path = '$'
	for (const part of enginePath.split('/')) {
		if (!part || part === '$') continue
		if (part.startsWith('[')) path += part
		else if (/^[A-Za-z_]+:/.test(part)) path += `[${part}]`
		else path += `.${part}`
	}
	return path
}

const seedSnapshot = (change: BranchStructureChangeDto): Record<string, unknown> => ({
	code: change.code ?? '',
	name: change.name ?? change.code ?? '',
})

const inferType = (
	snapshot: Record<string, unknown>,
	change: BranchStructureChangeDto,
): StructureType => {
	const paths = (change.diff?.changes ?? []).map((diff) => diff.fieldPath ?? '').join(' ')
	if (Array.isArray(snapshot.columns) || /(?:^|\$\.|\.)columns\[/.test(paths)) {
		return 'table'
	}
	const blob = `${JSON.stringify(change.diff ?? snapshot)} ${paths}`
	if (/expectationType|gxExpectation|odcsDqRules/i.test(blob)) {
		return 'expectation'
	}
	if (snapshot.nodeCount != null) return 'diagram'
	if (snapshot.sources != null || snapshot.sourceItems != null) return 'mapping'
	return 'table'
}

const liftDqRules = (snapshot?: Record<string, unknown>) => {
	if (!snapshot || !Array.isArray(snapshot.columns)) return
	const rules: unknown[] = []
	for (const column of snapshot.columns as Record<string, unknown>[]) {
		if (Array.isArray(column.odcsDqRules)) rules.push(...column.odcsDqRules)
	}
	if (rules.length) snapshot.odcsDqRules = rules
}

const applyMovedChanges = (
	change: BranchStructureChangeDto,
	base: Record<string, unknown> | undefined,
	source: Record<string, unknown> | undefined,
	target: Record<string, unknown> | undefined,
) => {
	for (const diff of change.diff?.changes ?? []) {
		if (diff.changeType !== 'MOVED') continue
		if (base) setPath(base, '$.Location', diff.oldValue)
		if (target) setPath(target, '$.Location', diff.oldValue)
		if (source) setPath(source, '$.Location', diff.newValue)
	}
}

export const changeToMrStructure = (
	change: BranchStructureChangeDto,
): MrStructure => {
	const base = seedSnapshot(change)
	const source = seedSnapshot(change)
	const target = seedSnapshot(change)
	const diffs = change.diff?.changes ?? []

	if (change.changeType === 'ADDED') {
		for (const diff of diffs) {
			if (diff.fieldPath) setPath(source, diff.fieldPath, diff.newValue)
		}
		liftDqRules(source)
		return toStructure(change, undefined, source, undefined)
	}
	if (change.changeType === 'REMOVED') {
		for (const diff of diffs) {
			if (diff.fieldPath) {
				setPath(base, diff.fieldPath, diff.oldValue)
				setPath(target, diff.fieldPath, diff.oldValue)
			}
		}
		liftDqRules(base)
		liftDqRules(target)
		return toStructure(change, base, undefined, target)
	}
	for (const diff of diffs) {
		if (!diff.fieldPath) continue
		if (diff.changeType !== 'ADDED') {
			setPath(base, diff.fieldPath, diff.oldValue)
			setPath(target, diff.fieldPath, diff.oldValue)
		}
		if (diff.changeType !== 'REMOVED') {
			setPath(source, diff.fieldPath, diff.newValue)
		}
	}
	// Conflicting fields are reported separately from `diff.changes` and carry all three sides.
	for (const conflict of change.conflicts ?? []) {
		if (!conflict.fieldPath) continue
		setPath(base, conflict.fieldPath, conflict.baseValue)
		setPath(target, conflict.fieldPath, conflict.oldValue)
		setPath(source, conflict.fieldPath, conflict.newValue)
	}
	applyMovedChanges(change, base, source, target)
	liftDqRules(base)
	liftDqRules(source)
	liftDqRules(target)
	return toStructure(change, base, source, target)
}

const historySnapshot = (
	history: HistoryWithDeploymentDto,
): Record<string, unknown> => {
	const data = (cloneJson(history.data) ?? {}) as Record<string, unknown>
	return {
		...data,
		code: history.code ?? data.code ?? '',
		name: history.name ?? data.name ?? history.code ?? '',
	}
}

/**
 * Builds the comparison from complete persisted snapshots. The change payload is
 * still used for metadata/conflict paths, but not to reconstruct structure data.
 */
export const changeToMrStructureFromHistories = (
	change: BranchStructureChangeDto,
	histories: ReadonlyMap<number, HistoryWithDeploymentDto>,
): MrStructure => {
	const fallback = changeToMrStructure(change)
	const persisted = (historyId: number | undefined) => {
		const history = historyId == null ? undefined : histories.get(historyId)
		return history ? historySnapshot(history) : undefined
	}
	const reconstructed = (snapshot: StructureSnapshot | undefined) =>
		snapshot as unknown as Record<string, unknown> | undefined

	if (change.changeType === 'ADDED') {
		return toStructure(
			change,
			undefined,
			persisted(change.currentVersion) ?? reconstructed(fallback.data.source),
			undefined,
		)
	}
	if (change.changeType === 'REMOVED') {
		const target =
			persisted(change.currentVersion) ?? reconstructed(fallback.data.target)
		return toStructure(change, target, undefined, target)
	}

	const base = persisted(change.ancestorVersion)
	const source = persisted(change.currentVersion)
	const target = persisted(change.comparedVersion)
	// Either every side is a real snapshot or none can be: pairing a complete snapshot with a
	// reconstructed one would report everything the change payload never mentioned as removed.
	if (!base || !source || !target) return fallback

	// Tree location is metadata on BranchTree, not part of persisted structure data.
	applyMovedChanges(change, base, source, target)
	return toStructure(change, base, source, target)
}

const toStructure = (
	change: BranchStructureChangeDto,
	base: Record<string, unknown> | undefined,
	source: Record<string, unknown> | undefined,
	target: Record<string, unknown> | undefined,
): MrStructure => ({
	key: String(change.structureId ?? change.code ?? change.name ?? ''),
	label: change.name ?? change.code ?? String(change.structureId ?? ''),
	type: inferType(source ?? target ?? base ?? {}, change),
	data: {
		base: base as unknown as StructureSnapshot,
		source: source as unknown as StructureSnapshot,
		target: target as unknown as StructureSnapshot,
	},
	dependsOn: [],
	conflictPaths: (change.conflicts ?? [])
		.map((conflict) => conflict.fieldPath)
		.filter((path): path is string => Boolean(path))
		.map(toEnginePath),
})

const applyResolutions = (
	structure: MrStructure,
	resolutions: MergeRequestConflictResolutionDtoRead[],
	structureId?: number,
): MrStructure => {
	if (!resolutions.length) return structure
	const tree = DiffEngine.buildDiffTree(
		structure.data.base,
		structure.data.source,
		structure.data.target,
	)
	const applied = new Set<string>()
	for (const resolution of resolutions) {
		if (
			resolution.structureId != null &&
			String(resolution.structureId) !== String(structureId ?? structure.key)
		) {
			continue
		}
		if (!resolution.fieldPath || !resolution.resolution) continue
		const resolutionKey = `${resolution.structureId ?? structureId ?? structure.key}#${resolution.fieldPath}`
		if (applied.has(resolutionKey)) continue
		applied.add(resolutionKey)
		let custom: unknown = resolution.selectedValue
		try {
			custom = resolution.selectedValue
				? JSON.parse(resolution.selectedValue)
				: resolution.selectedValue
		} catch {
			/* keep string */
		}
		const node = DiffEngine.findNode(tree, toEnginePath(resolution.fieldPath))
		if (!node) continue
		node.resolution = resolution.resolution as typeof node.resolution
		if (resolution.resolution === 'custom') node.customValue = custom
	}
	structure.tree = tree
	return structure
}

